import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import ProductCard from "@/components/ProductCard";
import { FABRIC_TYPES, FABRIC_TYPE_LABELS, FABRIC_TYPE_BLURBS } from "@/lib/taxonomy";
import { Scissors, Ribbon, Package, FileText, Layers, Globe, ArrowRight, Crown, Sparkles, X } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1681003564665-62848f8d481e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const IMG_HANDMADE = "https://images.unsplash.com/photo-1623310658847-33f12eaab710?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";
const IMG_ATELIER = "https://images.pexels.com/photos/17043766/pexels-photo-17043766.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const TYPE_ICON = {
  fabric: Scissors, lace_trim: Ribbon, notions: Package,
  pattern: FileText, heirloom_textile: Layers, cultural_fabric: Globe,
};

export default function Archives() {
  const [items, setItems] = useState([]);
  const [applyOpen, setApplyOpen] = useState(false);
  const [maker, setMaker] = useState({ name: "", email: "", portfolio: "", about: "" });
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    api.get("/listings", { params: { category: "fabric_archives", limit: 8 } })
      .then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const applyMaker = async () => {
    if (!maker.name.trim() || !maker.email.trim()) { toast.error("Add your name and email"); return; }
    setApplying(true);
    try {
      const { data } = await api.post("/maker-waitlist", maker);
      toast.success(data.already ? "You're already on the list — we'll be in touch!" : "You're on the maker waitlist — we'll be in touch!");
      setApplyOpen(false); setMaker({ name: "", email: "", portfolio: "", about: "" });
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setApplying(false); }
  };

  return (
    <div data-testid="archives-page">
      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(26,32,26,0.82) 0%, rgba(26,32,26,0.5) 60%, rgba(26,32,26,0.2) 100%)" }} />
        </div>
        <div className="relative max-w-[1600px] mx-auto px-6 lg:px-12 py-24 lg:py-32">
          <div className="max-w-2xl" style={{ color: "#F9F6F0" }}>
            <div className="text-[0.68rem] uppercase tracking-[0.24em] opacity-80">A new wing of Your Classic Style</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-3">
              <span className="font-antique italic">Fabric</span> Archives.
            </h1>
            <p className="mt-5 text-base leading-relaxed opacity-90 max-w-xl">
              An upscale archive of sewing fabric, antique lace, fine notions, heirloom textiles and culturally
              unique cloth — sourced for makers who care about provenance and quality.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/browse?category=fabric_archives" className="btn-klein" data-testid="archives-shop-btn">
                Explore the archive <ArrowRight size={14} />
              </Link>
              <Link to="/sell?category=fabric_archives"
                className="inline-flex items-center gap-2 px-6 py-3 text-xs uppercase tracking-[0.18em] border"
                style={{ borderColor: "rgba(249,246,240,0.6)", color: "#F9F6F0" }} data-testid="archives-list-btn">
                List in the archive
              </Link>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-14">
        {/* Sub-sections */}
        <div className="hairline-b pb-3">
          <div className="label-eyebrow">Browse the archive</div>
          <h2 className="font-display text-3xl tracking-tight mt-1">Six collections, one standard.</h2>
        </div>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="archive-sections">
          {FABRIC_TYPES.map((t) => {
            const Icon = TYPE_ICON[t] || Scissors;
            return (
              <Link key={t} to={`/browse?category=fabric_archives&product_type=${t}`}
                data-testid={`archive-section-${t}`}
                className="hairline p-6 bg-white group hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between">
                  <Icon size={20} strokeWidth={1.5} style={{ color: "#1E3A2B" }} />
                  <ArrowRight size={16} className="opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: "#1E3A2B" }} />
                </div>
                <h3 className="font-display text-lg mt-4">{FABRIC_TYPE_LABELS[t]}</h3>
                <p className="mt-1 text-sm text-neutral-500 leading-relaxed">{FABRIC_TYPE_BLURBS[t]}</p>
              </Link>
            );
          })}
        </div>

        {/* Shop the archive */}
        {items.length > 0 && (
          <section className="mt-16">
            <div className="hairline-b pb-3 flex items-baseline justify-between">
              <h2 className="font-display text-2xl tracking-tight">Fresh in the archive</h2>
              <Link to="/browse?category=fabric_archives" className="label-eyebrow underline" data-testid="archive-viewall">View all</Link>
            </div>
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
              {items.map((it) => <ProductCard key={it.id} item={it} />)}
            </div>
          </section>
        )}

        {/* Growth roadmap */}
        <section className="mt-20" data-testid="archive-growth">
          <div className="hairline-b pb-3">
            <div className="label-eyebrow" style={{ color: "#2563EB" }}>Growing soon</div>
            <h2 className="font-display text-3xl tracking-tight mt-1">Where the archive is headed.</h2>
            <p className="mt-2 text-sm text-neutral-500 max-w-2xl">
              The archive is built to grow. Next, we're opening dedicated homes for handmade heirloom clothing and for
              the makers who create it.
            </p>
          </div>
          <div className="mt-8 grid md:grid-cols-2 gap-4">
            <div className="hairline overflow-hidden bg-white" data-testid="growth-handmade">
              <div className="aspect-[16/9] overflow-hidden relative">
                <img src={IMG_HANDMADE} alt="" className="w-full h-full object-cover" />
                <span className="absolute top-3 left-3 text-[0.6rem] uppercase tracking-[0.16em] px-2 py-1"
                  style={{ background: "#2563EB", color: "#fff" }}>Coming soon</span>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2">
                  <Crown size={16} style={{ color: "#1E3A2B" }} />
                  <h3 className="font-display text-xl">Handmade &amp; Heirloom Clothing</h3>
                </div>
                <p className="mt-2 text-sm text-neutral-600 leading-relaxed">
                  Hand-sewn pieces crafted from archive fabrics — christening gowns, smocked sets, keepsake and
                  occasion wear, across every age.
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {["Baby", "Toddler", "Adult", "Pageant"].map((t) => (
                    <span key={t} className="chip" data-testid={`growth-tier-${t.toLowerCase()}`}>{t}</span>
                  ))}
                </div>
              </div>
            </div>

            <div className="hairline overflow-hidden bg-white" data-testid="growth-atelier">
              <div className="aspect-[16/9] overflow-hidden relative">
                <img src={IMG_ATELIER} alt="" className="w-full h-full object-cover" />
                <span className="absolute top-3 left-3 text-[0.6rem] uppercase tracking-[0.16em] px-2 py-1"
                  style={{ background: "#2563EB", color: "#fff" }}>Coming soon</span>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2">
                  <Sparkles size={16} style={{ color: "#1E3A2B" }} />
                  <h3 className="font-display text-xl">Artisan Ateliers</h3>
                </div>
                <p className="mt-2 text-sm text-neutral-600 leading-relaxed">
                  A vetted, invite-only space for upscale talent to design, make and sell their own bespoke clothing
                  and heirloom items — with their own storefront and following.
                </p>
                <button onClick={() => setApplyOpen(true)} data-testid="apply-maker-btn"
                  className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 text-xs uppercase tracking-[0.16em]"
                  style={{ background: "#2563EB", color: "#fff" }}>
                  Apply as a maker <ArrowRight size={13} />
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>

      {applyOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setApplyOpen(false)}>
          <div className="bg-white hairline p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="maker-modal">
            <div className="flex items-start justify-between">
              <div>
                <div className="label-eyebrow" style={{ color: "#2563EB" }}>Artisan Ateliers</div>
                <h2 className="font-display text-2xl mt-1">Apply as a maker</h2>
              </div>
              <button onClick={() => setApplyOpen(false)} className="p-1" data-testid="maker-close"><X size={16} /></button>
            </div>
            <p className="mt-2 text-xs text-neutral-500 leading-relaxed">
              Tell us about your craft. We'll reach out as we open the atelier wing to a first group of makers.
            </p>
            <div className="mt-5 space-y-3">
              <input className="w-full hairline p-3 text-sm" placeholder="Your name" data-testid="maker-name"
                value={maker.name} onChange={(e) => setMaker((m) => ({ ...m, name: e.target.value }))} />
              <input className="w-full hairline p-3 text-sm" placeholder="Email" data-testid="maker-email"
                value={maker.email} onChange={(e) => setMaker((m) => ({ ...m, email: e.target.value }))} />
              <input className="w-full hairline p-3 text-sm" placeholder="Portfolio / Instagram (optional)" data-testid="maker-portfolio"
                value={maker.portfolio} onChange={(e) => setMaker((m) => ({ ...m, portfolio: e.target.value }))} />
              <textarea rows={3} className="w-full hairline p-3 text-sm" placeholder="What do you make? (optional)" data-testid="maker-about"
                value={maker.about} onChange={(e) => setMaker((m) => ({ ...m, about: e.target.value }))} />
            </div>
            <button disabled={applying} onClick={applyMaker} className="btn-primary w-full mt-5" data-testid="maker-submit">
              {applying ? "Submitting…" : "Join the maker waitlist"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
